import { Link } from "react-router-dom";
import "./AdminPanelPage.css";

const EXAMPLE_SITES = [
    { name: "Shopify", text: "Online stores and checkouts" },
    { name: "WooCommerce", text: "WordPress shops" },
    { name: "Amazon", text: "Marketplace orders" },
    { name: "eBay", text: "Listings and sales" },
    { name: "Etsy", text: "Digital and handmade shops" },
    { name: "OpenCart", text: "Self-hosted catalogs" },
];

const SHOTS = [
    {
        src: "/admin-login.png",
        alt: "Sign-in screen of the admin panel",
        title: "Sign-in",
        caption: "Secure login for the team.",
    },
    {
        src: "/admin-pc.png",
        alt: "Dashboard with order statuses from connected sites",
        title: "Dashboard",
        caption: "Orders from every connected site in one feed.",
        wide: true,
    },
    {
        src: "/admin-catalog.png",
        alt: "Offers from a connected site with mapping status",
        title: "Offers",
        caption: "Switch the site, see its items, and check what is already linked.",
        wide: true,
    },
    {
        src: "/admin-map.png",
        alt: "Mappings from a site product to a supplier product",
        title: "Mappings",
        caption: "Link a product from any connected site to a supplier item.",
        wide: true,
    },
    {
        src: "/admin-message.png",
        alt: "Buyer message editor after a successful delivery",
        title: "Buyer message",
        caption: "A template for the buyer after the code is delivered. Placeholders fill in automatically.",
        wide: true,
    },
    {
        src: "/admin-team.png",
        alt: "Team access with owner and operator roles",
        title: "Team",
        caption: "Owner adds operators. The team sees orders and offers, not settings.",
        wide: true,
    },
    {
        src: "/admin-settings.png",
        alt: "Integration statuses for connected sites and the supplier",
        title: "Integrations",
        caption: "Each site and the supplier is a separate module with a live API status.",
        wide: true,
    },
];

function AdminPanelPage() {
    return (
        <div className="admin-panel-page">
            <div className="admin-panel-hero">
                <div className="admin-panel-hero__badge">Admin Panel</div>
                <h1 className="admin-panel-hero__title">
                    Connect different sales sites. Deliver orders automatically.
                </h1>
                <p className="admin-panel-hero__subtitle">
                    We built a custom admin panel for shops that sell on more than one website.
                    You connect the sites you already use — Shopify, WooCommerce, Amazon and more.
                    The panel takes the sale, matches the product, and sends the result back to the
                    buyer — without copy-paste.
                </p>
            </div>

            <div className="admin-panel-content">
                <section className="admin-panel-section">
                    <h2 className="admin-panel-section__title">Sites we can connect</h2>
                    <p className="admin-panel-section__text">
                        Support for popular sales sites. Each one is a separate module, so a new
                        store can be added without rebuilding the panel.
                    </p>
                    <div className="admin-panel-sites">
                        {EXAMPLE_SITES.map((site) => (
                            <article className="admin-panel-site" key={site.name}>
                                <h3 className="admin-panel-site__name">{site.name}</h3>
                                <p className="admin-panel-site__text">{site.text}</p>
                            </article>
                        ))}
                    </div>
                </section>

                <section className="admin-panel-section">
                    <h2 className="admin-panel-section__title">Key features</h2>
                    <div className="admin-panel-grid">
                        <div className="admin-panel-card">
                            <h3 className="admin-panel-card__title">Many sites</h3>
                            <p className="admin-panel-card__text">
                                Support for connecting Shopify, WooCommerce, Amazon, eBay, Etsy,
                                OpenCart and other stores to one panel.
                            </p>
                        </div>
                        <div className="admin-panel-card">
                            <h3 className="admin-panel-card__title">One feed</h3>
                            <p className="admin-panel-card__text">
                                Support for a single live feed of orders from all connected sites:
                                new, processing, done, error.
                            </p>
                        </div>
                        <div className="admin-panel-card">
                            <h3 className="admin-panel-card__title">Offers</h3>
                            <p className="admin-panel-card__text">
                                Items from every connected site in one list. Switch the site and see
                                what is linked and what is still open.
                            </p>
                        </div>
                        <div className="admin-panel-card">
                            <h3 className="admin-panel-card__title">Mappings</h3>
                            <p className="admin-panel-card__text">
                                One rule per product: this item on the site buys that item from the
                                supplier.
                            </p>
                        </div>
                        <div className="admin-panel-card">
                            <h3 className="admin-panel-card__title">Automation</h3>
                            <p className="admin-panel-card__text">
                                The site sends the order to the panel. The panel buys from the
                                supplier and returns the result to the buyer on that same site.
                            </p>
                        </div>
                        <div className="admin-panel-card">
                            <h3 className="admin-panel-card__title">Team access</h3>
                            <p className="admin-panel-card__text">
                                Intuitive roles for operators and owners, so the team sees only what
                                they need.
                            </p>
                        </div>
                    </div>
                </section>

                <section className="admin-panel-section">
                    <h2 className="admin-panel-section__title">How it was built</h2>
                    <p className="admin-panel-section__text">
                        React frontend and Node.js backend. Sales sites and the supplier stay in
                        separate adapters. A new site is a new folder and one line in the registry —
                        Offers and Mappings do not get a new if/else. The interface is dark and
                        compact for fast daily work.
                    </p>
                </section>

                <section className="admin-panel-section">
                    <h2 className="admin-panel-section__title">View the panel</h2>
                    <div className="admin-panel-shots">
                        {SHOTS.map((shot) => (
                            <figure
                                className={`admin-panel-shot${shot.wide ? " admin-panel-shot_wide" : ""}`}
                                key={shot.src}
                            >
                                <div className="admin-panel-shot__frame">
                                    <img
                                        className="admin-panel-shot__image"
                                        src={shot.src}
                                        alt={shot.alt}
                                        loading="lazy"
                                        decoding="async"
                                    />
                                </div>
                                <figcaption className="admin-panel-shot__meta">
                                    <strong className="admin-panel-shot__title">{shot.title}</strong>
                                    <span className="admin-panel-shot__caption">{shot.caption}</span>
                                </figcaption>
                            </figure>
                        ))}
                    </div>
                </section>

                <section className="admin-panel-section admin-panel-section_cta">
                    <h2 className="admin-panel-section__title">Want it like this for your needs?</h2>
                    <p className="admin-panel-section__text">
                        Tell us which sites you want to connect, and we will adapt the structure
                        and implementation to your requirements.
                    </p>
                    <div className="admin-panel-section_cta__actions">
                        <Link to="/contact" className="admin-panel-hero__button">
                            Discuss the project
                        </Link>
                    </div>
                </section>
            </div>
        </div>
    );
}

export default AdminPanelPage;
// pages/AdminPanelPage.js
